import type * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Scale } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { getCampaignCommercials } from "@/api/cpa";
import { dealSummary } from "@/lib/deals";
import { formatUsdShort } from "@/lib/money";
import type { DealDto } from "@/types/cpa";

/**
 * The "Commercials" card on campaign detail: the RESOLVED deal per action
 * type on each side (inherited global default vs campaign override, clearly
 * labelled) plus a margin preview when both sides carry a flat amount.
 */
export function CampaignCommercialsCard({ campaignId }: { campaignId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["campaign-commercials", campaignId],
        queryFn: () => getCampaignCommercials(campaignId),
        retry: false,
    });

    return (
        <div className="rounded-md border border-border bg-card p-4" data-testid="campaign-commercials-card">
            <div className="flex items-center gap-2 text-sm font-medium">
                <Scale className="h-4 w-4" /> Commercials
            </div>
            {q.isLoading ? (
                <div className="flex justify-center py-6">
                    <Spinner />
                </div>
            ) : !q.data || q.data.actions.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">
                    No deals apply to this campaign yet. Add them on the{" "}
                    {q.data?.advertiserId != null ? (
                        <Link to={`/advertisers/${q.data.advertiserId}`} className="underline underline-offset-2">
                            advertiser
                        </Link>
                    ) : (
                        "advertiser"
                    )}{" "}
                    or{" "}
                    {q.data?.affiliateId != null ? (
                        <Link to={`/affiliates/${q.data.affiliateId}`} className="underline underline-offset-2">
                            affiliate
                        </Link>
                    ) : (
                        "affiliate"
                    )}{" "}
                    page.
                </p>
            ) : (
                <div className="mt-3 space-y-3">
                    {q.data.actions.map((a) => (
                        <div
                            key={a.actionType}
                            className="rounded-md border border-border/60 p-3"
                            data-testid={`commercials-action-${a.actionType}`}
                        >
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                Per {a.actionType === "ftd" ? "FTD" : a.actionType}
                            </div>
                            <div className="mt-2 space-y-1.5 text-sm">
                                <SideLine label="Money in" deal={a.advertiserDeal} counterpartyLink={
                                    q.data.advertiserId != null ? `/advertisers/${q.data.advertiserId}` : null
                                } />
                                <SideLine label="Money out" deal={a.affiliateDeal} counterpartyLink={
                                    q.data.affiliateId != null ? `/affiliates/${q.data.affiliateId}` : null
                                } />
                            </div>
                            <MarginLine
                                actionType={a.actionType}
                                advertiserDeal={a.advertiserDeal}
                                affiliateDeal={a.affiliateDeal}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SideLine({
    label,
    deal,
    counterpartyLink,
}: {
    label: string;
    deal: DealDto | null;
    counterpartyLink: string | null;
}): React.ReactNode {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{label}</span>
            {deal == null ? (
                <span className="text-xs text-muted-foreground">
                    No deal —{" "}
                    {counterpartyLink != null ? (
                        <Link to={counterpartyLink} className="underline underline-offset-2">
                            add one
                        </Link>
                    ) : (
                        "no counterparty assigned"
                    )}
                </span>
            ) : (
                <>
                    <span>{dealSummary(deal)}</span>
                    <Badge variant={deal.campaignId != null ? "secondary" : "outline"}>
                        {deal.campaignId != null ? "Campaign override" : "Global default"}
                    </Badge>
                </>
            )}
        </div>
    );
}

/** "revenue $250 − payout $200 = margin $50 per FTD" — only when both sides carry flat amounts. */
function MarginLine({
    actionType,
    advertiserDeal,
    affiliateDeal,
}: {
    actionType: string;
    advertiserDeal: DealDto | null;
    affiliateDeal: DealDto | null;
}): React.ReactNode {
    const revenue = advertiserDeal?.cpaUsdMicros;
    const payout = affiliateDeal?.cpaUsdMicros;
    if (revenue == null || payout == null) return null;
    const margin = revenue - payout;
    const noun = actionType === "ftd" ? "FTD" : actionType;
    return (
        <div
            className={`mt-2 border-t border-border/60 pt-2 text-xs font-medium tabular-nums ${
                margin >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
            }`}
            data-testid={`commercials-margin-${actionType}`}
        >
            revenue {formatUsdShort(revenue)} − payout {formatUsdShort(payout)} = margin {formatUsdShort(margin)} per {noun}
        </div>
    );
}
